import {
  isRbacV2ActionAllowed,
  isRbacV2ScopeAllowed,
  type RbacV2Resource,
  type RbacV2RolePermission,
} from './rbac.v2.js'
import { type HrPopulation, parseHrPopulation } from './rbac.population.js'
import { hasOwn } from './internal/hasOwn.js'

/**
 * Grant v2 tel qu'il est résolu pour l'acteur courant : la permission, la population qu'elle
 * couvre, et le périmètre boutique de l'affectation qui l'a produite. Un grant est indissociable :
 * on ne croise jamais l'action de l'un avec le périmètre d'un autre.
 */
export interface CurrentAdminRbacV2Grant extends RbacV2RolePermission {
  /** Périmètre de l'affectation : `all` ou la liste explicite des boutiques. */
  storeIds?: 'all' | number[]
  /** Population couverte ; `null` ou absent vaut tous les postes et tous les types de contrat. */
  population?: HrPopulation | null
  /** Affectation d'origine — deux affectations du même rôle produisent deux grants distincts. */
  adminRoleId?: number
}

/**
 * Clés de l'ancien double régime que `GET /rbac/me/permissions` envoie encore, figées : la v2 est
 * l'unique système de droits et l'interrupteur v1 n'existe plus. Elles ne décident de rien ; le
 * backend les garde pour les BO déjà déployés, dont le parseur les lit encore.
 */
export const RBAC_SESSION_LEGACY_CONSTANTS = {
  enforcementEnabled: false,
  permissionV2Enabled: true,
} as const

/**
 * Contrat `GET /rbac/me/permissions`. Les constantes héritées sont facultatives : le BO ne les lit
 * plus, et le backend pourra les retirer sans casser un BO déployé. Elles sont typées `boolean`,
 * comme le parseur les accepte : il reste tolérant sur leur valeur.
 */
export interface CurrentAdminRbacPermissionsResponse extends Partial<
  Record<keyof typeof RBAC_SESSION_LEGACY_CONSTANTS, boolean>
> {
  schemaVersion: 2
  authorizationModel: 'rbac-v2'
  /** Grants v2 (taxonomie `rbac.v2.ts`) — seule source d'autorisation du BO. */
  grantsV2: CurrentAdminRbacV2Grant[]
}

const hasExactKeys = (
  value: Record<string, unknown>,
  required: readonly string[],
  optional: readonly string[] = [],
): boolean => {
  const allowed = new Set([...required, ...optional])
  return (
    required.every((key) => hasOwn(value, key)) &&
    Object.keys(value).every((key) => allowed.has(key))
  )
}

const isOptionalPositiveInteger = (value: unknown): boolean =>
  value === undefined || (Number.isInteger(value) && Number(value) > 0)

/** Périmètre d'affectation porté par un grant : `all` ou une liste explicite d'identifiants. */
const isOptionalStoreIds = (value: unknown): boolean =>
  value === undefined ||
  value === 'all' ||
  (Array.isArray(value) && value.every((id) => Number.isInteger(id) && Number(id) > 0))

/** Forme d'un grant v2 : clés exactes et types, sans regarder la taxonomie. */
const isV2GrantShape = (value: unknown): value is CurrentAdminRbacV2Grant => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false
  const grant = value as Record<string, unknown>
  // Les conditions RH de l'ancien modèle n'ont plus de clé : `hasExactKeys` les refuse avec le
  // reste des clés inconnues, sans jamais les réinterpréter en population.
  return (
    hasExactKeys(
      grant,
      ['resource', 'action'],
      ['scope', 'roleId', 'permissionId', 'storeIds', 'population', 'adminRoleId'],
    ) &&
    typeof grant.resource === 'string' &&
    typeof grant.action === 'string' &&
    (grant.scope === undefined || typeof grant.scope === 'string') &&
    isOptionalPositiveInteger(grant.roleId) &&
    isOptionalPositiveInteger(grant.permissionId) &&
    isOptionalPositiveInteger(grant.adminRoleId) &&
    isOptionalStoreIds(grant.storeIds)
  )
}

/** Un grant v2 est connu quand la taxonomie porte son couple, son périmètre et sa population. */
const isKnownV2Grant = (grant: CurrentAdminRbacV2Grant): boolean =>
  isRbacV2ActionAllowed(grant.resource, grant.action) &&
  isRbacV2ScopeAllowed(grant.resource, grant.action, grant.scope ?? 'global') &&
  parseHrPopulation(grant.population, grant.resource as RbacV2Resource).ok

const isBoolean = (value: unknown): boolean => typeof value === 'boolean'

/**
 * Clés héritées que le parseur tolère sans les lire, avec leur contrôle de type quand elles sont
 * présentes : les deux constantes de l'ancien double régime (`RBAC_SESSION_LEGACY_CONSTANTS`), et
 * `grants`, la liste des grants v1 qu'envoie encore un backend antérieur au retrait de la taxonomie
 * v1 (BO déployé avant lui).
 */
const LEGACY_CONTRACT_FIELDS: Record<string, (value: unknown) => boolean> = {
  ...Object.fromEntries(Object.keys(RBAC_SESSION_LEGACY_CONSTANTS).map((key) => [key, isBoolean])),
  grants: Array.isArray,
}

/**
 * Parse le DTO de session BO avant toute installation d'ability.
 *
 * Strict sur la **structure** (clés connues, types) : une réponse mal formée rend `null` et le BO
 * reste sur son dernier contrat exploitable. Les clés héritées (`LEGACY_CONTRACT_FIELDS`) sont
 * facultatives et jamais lues : absentes, le contrat reste exploitable ; présentes, elles gardent
 * leur type.
 *
 * Tolérant sur le **catalogue** : un grant dont la ressource, l'action, le périmètre ou la
 * population n'existent pas (ligne `role_permissions` survivant à une ressource retirée du code,
 * BO déployé avant le backend) est simplement écarté — il ne donne aucun droit — et le reste du
 * contrat reste valide.
 *
 * Sans grant écarté, la réponse est renvoyée telle quelle : son identité suit celle de la donnée
 * React Query, ce qui garde stable la mémorisation du contrat dans `GlobalProvider`.
 */
export const parseCurrentAdminRbacPermissionsResponse = (
  value: unknown,
): CurrentAdminRbacPermissionsResponse | null => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null
  const response = value as Record<string, unknown>
  if (
    !hasExactKeys(
      response,
      ['schemaVersion', 'authorizationModel', 'grantsV2'],
      Object.keys(LEGACY_CONTRACT_FIELDS),
    ) ||
    response.schemaVersion !== 2 ||
    response.authorizationModel !== 'rbac-v2' ||
    !Object.entries(LEGACY_CONTRACT_FIELDS).every(
      ([key, isValid]) => response[key] === undefined || isValid(response[key]),
    ) ||
    !Array.isArray(response.grantsV2) ||
    !response.grantsV2.every(isV2GrantShape)
  ) {
    return null
  }
  const contract = response as unknown as CurrentAdminRbacPermissionsResponse
  const grantsV2 = contract.grantsV2.filter(isKnownV2Grant)
  return grantsV2.length === contract.grantsV2.length ? contract : { ...contract, grantsV2 }
}

export interface RbacRoleAdmin {
  id: number
  email: string
  firstName: string
  lastName: string
}

/**
 * Affectation d'un rôle à un compte BO, avec son périmètre boutique. `allStores: true` implique une
 * liste vide ; `allStores: false` implique au moins une boutique.
 */
export interface RbacRoleAssignment {
  adminId: number
  allStores: boolean
  storeIds: number[]
}

/** Élément du dialogue « Assigner des utilisateurs » d'un rôle (`PUT /roles/:id/admins`). */
export interface RbacRoleAssignmentInput {
  adminId: number
  allStores: boolean
  storeIds: number[]
}

/** Élément de la section « Rôles » d'une fiche compte BO (`PUT /admins/:id/roles`). */
export interface RbacAdminRoleAssignmentInput {
  roleId: number
  allStores: boolean
  storeIds: number[]
}

export interface ReplaceRbacRoleAssignmentsRequest {
  assignments: RbacRoleAssignmentInput[]
}

export interface ReplaceRbacAdminRoleAssignmentsRequest {
  assignments: RbacAdminRoleAssignmentInput[]
}

export interface RbacRole {
  id: number
  name: string
  description: string | null
  /** Famille de rangement (`role_families.key`), `null` = « Sans famille ». Aucun droit conféré. */
  familyKey: string | null
  /**
   * Rôle « Full Access » : toute la taxonomie v2, resynchronisée à chaque déploiement. Le serveur
   * refuse d'en modifier les permissions ; l'éditeur les montre en lecture seule. Absent sur un
   * serveur antérieur, ce qui vaut `false`.
   */
  fullAccess?: boolean
  /** Permissions v2 (taxonomie `rbac.v2.ts`) — vide tant que le rôle n'a pas de droits v2. */
  permissionsV2: RbacV2RolePermission[]
  admins: RbacRoleAdmin[]
  /** Périmètre de chaque affectation du rôle ; absent tant que le serveur ne le sérialise pas. */
  assignments?: RbacRoleAssignment[]
}

/**
 * `adminIds` a disparu de la création : une liste plate sans périmètre y créait des affectations
 * « toutes boutiques » implicites (audit du 19/09, B4). Une affectation posée à la création porte
 * désormais son périmètre, comme dans le dialogue « Assigner des utilisateurs ».
 */
export interface CreateRbacRoleRequest {
  name: string
  description?: string | null
  permissionsV2?: RbacV2RolePermission[]
  assignments?: RbacRoleAssignmentInput[]
}

/**
 * Compte affectable, tel que le rend `GET /rbac/assignable-admins` : le strict nécessaire du
 * dialogue « Assigner des utilisateurs », servi sous `edit roles-admin.roles.admin-assignment`
 * plutôt que sous `read users.bo-users.user` (MAJ-05).
 */
export interface RbacAssignableAdmin {
  id: number
  firstName: string
  lastName: string
  email: string
}

/**
 * Option du sélecteur « développeur » d'un projet foncier (`GET /real-estate/developers`) :
 * l'éligibilité « Prez projet » est calculée côté serveur, et rien d'autre que le nom ne sort
 * (M2 / M3 / TAX-B1).
 */
export interface RealEstateDeveloperOption {
  id: number
  firstName: string
  lastName: string
}

export interface UpdateRbacRoleRequest {
  name?: string
  description?: string | null
  /** Famille de rangement du rôle ; `null` le remet « Sans famille ». */
  familyKey?: string | null
}

export interface ReplaceRbacRoleAdminsRequest {
  adminIds: number[]
}

/**
 * Row returned by GET /feature-flags (Lucid JSON: camelCase, ISO date strings).
 * Aligned with `TimestampsAndID` without importing `index.ts` (avoids circular export).
 */
export interface FeatureFlag {
  id: number
  createdAt: string
  updatedAt: string
  name: string
  enabled: boolean
}
